import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { product, qualityCommitments, qualityDisclosures } from "../content/site";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Eyebrow, MaskLine } from "../components/Reveal";

const formulationPoints = [
  "100% Magnesium Bisglycinate",
  "Fully reacted",
  "Non-buffered",
];

const principles = [
  {
    title: "A single active ingredient, done properly",
    body: "We would rather give one formula our full attention than fill a range simply for the sake of having more products.",
    emphasis: "Focus is a quality decision.",
  },
  {
    title: "Plain-English labels",
    body: "No proprietary blends. No unnecessarily complicated ingredient lists. We want you to be able to understand what you are taking, how much you are taking and what is in the bottle.",
  },
  {
    title: "Honest communication",
    body: "We do not believe wellness needs fear, hype or exaggerated promises. We communicate clearly about what our products contain and stay within the authorised claims permitted for food supplements.",
  },
  {
    title: "UK-first",
    body: "EL Well is a UK brand built with UK customers in mind — from our product choices and packaging to the standards and regulations that guide how we communicate.",
  },
];

function CompareRow({ label, value, strong = false }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-400">{label}</p>
      <p className={`mt-1.5 text-sm leading-relaxed ${strong ? "font-semibold text-stone-900" : "text-stone-600"}`}>
        {value}
      </p>
    </div>
  );
}

export default function Why() {
  return (
    <div data-testid="why-page">
      <PageHeader
        eyebrow="Why EL Well"
        title="Chosen, not accumulated."
        body="Every decision behind an EL Well bottle — the form, the dose, the ingredients and the label — is made deliberately. We believe supplements should be straightforward: thoughtfully formulated, clearly explained and easy to understand. Here is what that means in practice."
      />

      {/* THE FORM MATTERS */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="why-comparison">
        <Reveal>
          <Eyebrow>The form matters</Eyebrow>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600">
            Not all forms of magnesium are the same.
          </p>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-stone-600">
            For Nº 01, we chose magnesium bisglycinate — a form of magnesium bound to glycine.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 max-w-2xl border-l-2 border-emerald-900/40 pl-8">
            <h2 className="font-serif text-3xl font-light text-stone-900 sm:text-4xl">Glycinate or bisglycinate?</h2>
            <p className="mt-5 text-base leading-relaxed text-stone-600">
              Magnesium bisglycinate is often referred to simply as{" "}
              <span className="font-semibold text-stone-900">“magnesium glycinate.”</span>
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">
              EL Well Nº 01 uses <span className="font-semibold text-stone-900">magnesium bisglycinate</span>. We use{" "}
              <span className="font-semibold text-stone-900">Magnesium Glycinate</span> as the familiar product name
              while clearly stating the precise form used in the formulation.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-12 max-w-2xl text-base leading-relaxed text-stone-600">
            Here is how our chosen form compares with some other commonly used forms, in plain terms.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-px overflow-hidden border border-stone-900/10 bg-stone-900/10 md:grid-cols-3">
          {/* EL Well Glycinate — highlighted */}
          <Reveal className="bg-[#F3EFE6]" data-testid="comparison-card-glycinate">
            <div className="flex h-full flex-col p-8 lg:p-10">
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-900">
                EL Well Magnesium Glycinate
              </p>
              <div className="mt-7 space-y-6">
                <CompareRow label="Form" value="Magnesium bisglycinate — a chelated form bound to glycine" />
                <CompareRow label="Why we chose it" value="It fits our approach to simple, considered formulation" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-400">Our formulation</p>
                  <ul className="mt-3 space-y-2.5">
                    {formulationPoints.map((p) => (
                      <li key={p} className="flex items-center gap-2.5 text-sm font-semibold text-stone-900">
                        <Check className="h-4 w-4 shrink-0 text-emerald-900" strokeWidth={1.5} />
                        {p}
                      </li>
                    ))}
                    <li>
                      <span
                        data-testid="no-oxide-badge"
                        className="inline-flex items-center gap-2 border border-emerald-900/30 bg-[#E9F0EA] px-3.5 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-emerald-900"
                      >
                        <Check className="h-3.5 w-3.5" strokeWidth={2} />
                        No magnesium oxide
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Magnesium Oxide */}
          <Reveal delay={0.08} className="bg-[#FBF9F5]" data-testid="comparison-card-oxide">
            <div className="flex h-full flex-col p-8 lg:p-10">
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-stone-500">
                Magnesium Oxide
              </p>
              <div className="mt-7 space-y-6">
                <CompareRow label="Form" value="Inorganic magnesium compound" />
                <CompareRow label="Commonly found in" value="Many magnesium supplements and combination formulas" />
                <CompareRow label="Our approach" value="Not used in EL Well Nº 01" strong />
              </div>
            </div>
          </Reveal>

          {/* Magnesium Citrate */}
          <Reveal delay={0.16} className="bg-[#FBF9F5]" data-testid="comparison-card-citrate">
            <div className="flex h-full flex-col p-8 lg:p-10">
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-stone-500">
                Magnesium Citrate
              </p>
              <div className="mt-7 space-y-6">
                <CompareRow label="Form" value="Magnesium bound to citric acid" />
                <CompareRow label="Commonly used as" value="Another established form of supplemental magnesium" />
                <CompareRow label="Our approach" value="A recognised alternative, but not the form we selected for Nº 01" />
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-xs italic leading-relaxed text-stone-500">
            This comparison is provided for general information and is not medical advice. Individual requirements
            vary. Speak to a healthcare professional if you are unsure which supplement is appropriate for you.
          </p>
        </Reveal>
      </section>

      {/* OUR PRINCIPLES */}
      <section className="border-t border-stone-900/10 bg-[#F3EFE6]" data-testid="why-principles">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
          <Reveal>
            <Eyebrow>Our principles</Eyebrow>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-stone-900/10 bg-stone-900/10 sm:grid-cols-2">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} className="bg-[#FBF9F5]">
                <div className="h-full p-10 lg:p-12">
                  <p className="font-serif text-2xl font-light text-stone-300">0{i + 1}</p>
                  <h3 className="mt-4 font-serif text-2xl font-light text-stone-900">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{p.body}</p>
                  {p.emphasis && (
                    <p className="mt-4 text-sm font-semibold text-stone-900">{p.emphasis}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY & TRANSPARENCY */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="why-quality-section">
        <Reveal>
          <Eyebrow>Quality & Transparency</Eyebrow>
          <p className="mt-6 max-w-2xl font-serif text-3xl font-light leading-snug text-stone-900 sm:text-4xl">
            Nothing to hide.
          </p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-600">
            Trust is earned with evidence, not adjectives. These are the commitments every EL Well product is held to
            — and the things we will publish as they become real.
          </p>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-stone-900/10 bg-stone-900/10 sm:grid-cols-2 lg:grid-cols-4">
          {qualityCommitments.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="bg-[#FBF9F5]">
              <div className="h-full p-8 lg:p-10">
                <p className="font-serif text-2xl font-light text-stone-300">0{i + 1}</p>
                <h3 className="mt-4 font-serif text-xl font-light text-stone-900 sm:text-2xl">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-14 max-w-3xl border border-dashed border-stone-900/30 bg-[#F3EFE6] p-8 lg:p-10">
            <p className="font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-emerald-900/80">
              Certificates & Testing
            </p>
            <h3 className="mt-4 font-serif text-2xl font-light text-stone-900">{qualityDisclosures.title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-stone-600">{qualityDisclosures.body}</p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-14 max-w-2xl text-xs leading-relaxed text-stone-500">
            Food supplements are not medicines. EL Well products are not intended to diagnose, treat, cure or prevent
            any disease, and we will only ever use health claims that are authorised for use in Great Britain, worded
            exactly as approved. If you are pregnant, breastfeeding, taking medication or under medical supervision,
            speak to a healthcare professional before taking any supplement.
          </p>
        </Reveal>
      </section>

      {/* CLOSING — Nº 01 */}
      <section className="bg-[#1E3A2D] text-stone-100" data-testid="why-closing">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
          <Reveal>
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-emerald-100/70">
              Nº 01 — Magnesium Glycinate
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-tight text-stone-50 sm:text-5xl">
              Our first product reflects the EL Well philosophy:
            </h2>
            <p className="mt-8 max-w-2xl font-serif text-2xl font-light leading-snug text-stone-100 sm:text-3xl">
              100% Magnesium Bisglycinate. Fully reacted. Non-buffered. No magnesium oxide.
            </p>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-300">
              A clear formulation, thoughtfully chosen. No extra active ingredients added simply to make the formula
              look more impressive.
            </p>
          </Reveal>
          <h3 className="mt-14 font-serif text-4xl font-light leading-tight text-stone-50 sm:text-5xl">
            <MaskLine inView>
              <span className="italic">
                Wellness, simply.
                <img
                  src="/assets/favicon.png"
                  alt=""
                  aria-hidden="true"
                  className="ml-2 inline-block h-[0.5em] w-auto -translate-y-[0.05em]"
                />
              </span>
            </MaskLine>
          </h3>
          <Reveal delay={0.15}>
            <Link
              to={`/product/${product.slug}`}
              data-testid="why-view-product-cta"
              className="mt-12 inline-block border border-stone-100/40 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-100 transition-colors duration-300 hover:bg-stone-50 hover:text-stone-900"
            >
              View Product
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
